import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import api, { fmtDate, formatApiError, fmtMoney } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Plus, FileText, Trash2, Send, CheckCircle2, XCircle, FileSpreadsheet, Eye, Loader2 } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import AttachmentsPanel from "../components/AttachmentsPanel";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/rbac";

export default function PurchaseRequests() {
    const { user } = useAuth();
    const canCreate = can(user, "pr.create");
    const canApprove = can(user, "pr.approve");
    const canRfq = can(user, "rfq.create");
    const [items, setItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [projects, setProjects] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null); // pr object
    const [rfqOpen, setRfqOpen] = useState(false);
    const [rfqSuppliers, setRfqSuppliers] = useState([]);
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const statusFilter = searchParams.get("status") || "";
    const filteredItems = statusFilter
        ? items.filter((p) => p.status === statusFilter)
        : items;

    const loadAll = async () => {
        const results = await Promise.allSettled([
            api.get("/purchase-requests"),
            api.get("/products"),
            api.get("/projects"),
            api.get("/suppliers"),
        ]);
        const [prs, pp, pj, ss] = results;
        setItems(prs.status === "fulfilled" ? prs.value.data : []);
        setProducts(pp.status === "fulfilled" ? pp.value.data : []);
        setProjects(pj.status === "fulfilled" ? pj.value.data : []);
        setSuppliers(ss.status === "fulfilled" ? ss.value.data : []);
    };
    useEffect(() => { loadAll(); }, []);

    const submit = async ({ payload, files, submitNow }) => {
        try {
            const { data: pr } = await api.post("/purchase-requests", {
                ...payload,
                submit_now: !!submitNow,
            });
            // Upload staged attachments (best-effort; PR is already created)
            if (files && files.length) {
                for (const f of files) {
                    try {
                        const fd = new FormData();
                        fd.append("file", f);
                        await api.post(`/attachments/pr/${pr.id}`, fd, {
                            headers: { "Content-Type": "multipart/form-data" },
                        });
                    } catch (e) {
                        toast.error(`${f.name}: ${formatApiError(e)}`);
                    }
                }
            }
            toast.success(submitNow ? `Talep ${pr.number} onaya gönderildi` : `Taslak ${pr.number} kaydedildi`);
            setOpen(false); loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doAction = async (pr, action, body = {}) => {
        try {
            await api.post(`/purchase-requests/${pr.id}/${action}`, body);
            toast.success("İşlem başarılı");
            const fresh = await api.get(`/purchase-requests/${pr.id}`);
            setDetail(fresh.data);
            loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const createRfq = async () => {
        try {
            const res = await api.post("/rfqs", {
                pr_id: detail.id,
                supplier_ids: rfqSuppliers,
                notes: "",
            });
            toast.success(`RFQ ${res.data.number} oluşturuldu`);
            setRfqOpen(false); setDetail(null);
            nav(`/rfqs/${res.data.id}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell
            title="Satın Alma Talepleri"
            subtitle="Satın Alma"
            actions={
                canCreate ? (
                <NewPRButton
                    open={open} setOpen={setOpen}
                    user={user}
                    products={products} projects={projects}
                    onSubmit={submit}
                />
                ) : null
            }
        >
            {statusFilter && (
                <div
                    data-testid="pr-status-filter-chip"
                    className="mb-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-md px-3 py-2 text-sm"
                >
                    <span>Filtre: <strong>Onay bekleyen</strong> talepler</span>
                    <button
                        data-testid="pr-clear-status-filter"
                        onClick={() => setSearchParams({})}
                        className="text-amber-800 hover:text-amber-900 underline text-xs"
                    >
                        Temizle
                    </button>
                </div>
            )}
            <div data-testid="pr-page" className="bg-card border border-border rounded-md">
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>No</th><th>Tarih</th><th>Departman</th><th>Proje</th>
                                <th>Talep Eden</th><th>Öncelik</th>
                                <th className="text-center">Kalem</th>
                                <th>Durum</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.map((p) => (
                                <tr key={p.id} data-testid={`pr-row-${p.number}`} className="cursor-pointer" onClick={() => setDetail(p)}>
                                    <td className="font-mono text-xs">{p.number}</td>
                                    <td>{fmtDate(p.date)}</td>
                                    <td>{p.department || "—"}</td>
                                    <td className="text-xs">{projects.find(pr=>pr.id===p.project_id)?.name || "—"}</td>
                                    <td>{p.requested_by_name}</td>
                                    <td><StatusBadge status={p.priority}/></td>
                                    <td className="text-center tabular-nums">{p.lines?.length || 0}</td>
                                    <td><StatusBadge status={p.status}/></td>
                                    <td><Button size="sm" variant="ghost"><Eye size={14}/></Button></td>
                                </tr>
                            ))}
                            {filteredItems.length === 0 && (
                                <tr><td colSpan={9} className="text-center py-16 text-muted-foreground">
                                    <FileText size={28} className="mx-auto mb-2 opacity-40"/>
                                    {statusFilter ? "Bu duruma uygun talep yok" : "Henüz talep yok"}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail dialog */}
            <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    {detail && (
                        <>
                            <DialogHeader>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <DialogTitle className="flex items-center gap-3">
                                            <span className="font-mono text-sm">{detail.number}</span>
                                            <StatusBadge status={detail.status}/>
                                            <StatusBadge status={detail.priority}/>
                                        </DialogTitle>
                                        <DialogDescription className="mt-1">
                                            {fmtDate(detail.date)} • {detail.requested_by_name}
                                        </DialogDescription>
                                    </div>
                                </div>
                            </DialogHeader>
                            <div className="grid grid-cols-3 gap-4 text-sm py-2 border-y border-border">
                                <Info label="Talep Eden" value={detail.requested_by_name || "—"}/>
                                <Info label="Departman" value={detail.department || "—"}/>
                                <Info label="Proje" value={projects.find(pr=>pr.id===detail.project_id)?.name || "Genel Gider / Projesiz"}/>
                                <Info label="Gerekli Tarih" value={fmtDate(detail.required_date)}/>
                                <Info label="Teslim Yeri" value={detail.delivery_location || "—"}/>
                                <Info label="Öncelik" value={detail.priority || "—"}/>
                            </div>
                            {detail.priority === "urgent" && detail.urgency_reason && (
                                <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded-md px-3 py-2">
                                    <b>Acil Gerekçesi:</b> {detail.urgency_reason}
                                </div>
                            )}
                            <ApprovalChainCard detail={detail} />
                            <div>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Kalemler</div>
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Ürün / Malzeme</th>
                                            <th>Teknik Açıklama</th>
                                            <th className="text-right">Miktar</th>
                                            <th>Birim</th>
                                            <th className="text-right">Birim Fiyat</th>
                                            <th>Para</th>
                                            <th className="text-right">Satır Toplamı</th>
                                            <th>Marka/Model</th>
                                            <th className="text-center">Alt.OK</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.lines?.map((l) => {
                                            const ccy = l.currency || "TRY";
                                            const total = (Number(l.quantity) || 0) * (Number(l.estimated_price) || 0);
                                            return (
                                                <tr key={l.line_id}>
                                                    <td className="font-medium">{l.name}{l.sku && l.sku !== "—" ? <span className="text-xs text-muted-foreground ml-1">({l.sku})</span> : null}</td>
                                                    <td className="text-xs text-muted-foreground max-w-[200px] truncate" title={l.description || ""}>{l.description || "—"}</td>
                                                    <td className="text-right tabular-nums">{l.quantity}</td>
                                                    <td>{l.unit}</td>
                                                    <td className="text-right tabular-nums">{fmtMoney(l.estimated_price, ccy)}</td>
                                                    <td className="text-xs">{ccy}</td>
                                                    <td className="text-right tabular-nums font-medium">{fmtMoney(total, ccy)}</td>
                                                    <td className="text-xs">{l.brand_model || "—"}</td>
                                                    <td className="text-center text-xs">{l.alternative_ok === true ? "Evet" : l.alternative_ok === false ? "Hayır" : "—"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {detail.notes && <div className="text-sm"><b>Notlar:</b> {detail.notes}</div>}
                            <AttachmentsPanel entityType="pr" entityId={detail.id}/>
                            <DialogFooter className="gap-2 flex-wrap">
                                {detail.status === "draft" && canCreate && (
                                    <Button data-testid="pr-submit" variant="outline" onClick={()=>doAction(detail, "submit")}>
                                        <Send size={14} className="mr-2"/> Onaya Gönder
                                    </Button>
                                )}
                                {detail.status === "waiting_approval" && canApprove && (detail.requested_by !== user?.id || user?.role === "admin") && (
                                    <>
                                        <Button data-testid="pr-reject" variant="outline" className="text-rose-600" onClick={()=>{
                                            const reason = window.prompt("Ret gerekçesi (zorunlu):", "");
                                            if (!reason || !reason.trim()) {
                                                toast.error("Ret gerekçesi zorunludur");
                                                return;
                                            }
                                            doAction(detail, "reject", { reason: reason.trim() });
                                        }}>
                                            <XCircle size={14} className="mr-2"/> Reddet
                                        </Button>
                                        <Button data-testid="pr-approve" onClick={()=>doAction(detail, "approve")}>
                                            <CheckCircle2 size={14} className="mr-2"/> Onayla
                                        </Button>
                                    </>
                                )}
                                {detail.status === "waiting_approval" && canApprove && detail.requested_by === user?.id && user?.role !== "admin" && (
                                    <div data-testid="pr-self-approve-warning" className="text-xs text-amber-600 border border-amber-200 bg-amber-50 rounded-md px-3 py-2">
                                        Kendi oluşturduğunuz talebi onaylayamazsınız. Onay başka bir Yönetici/Müdür tarafından verilmelidir.
                                    </div>
                                )}
                                {detail.status === "approved" && canRfq && (
                                    <Button data-testid="pr-to-rfq" onClick={()=>{ setRfqSuppliers([]); setRfqOpen(true); }}>
                                        <FileSpreadsheet size={14} className="mr-2"/> RFQ Oluştur
                                    </Button>
                                )}
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* RFQ Suppliers picker */}
            <Dialog open={rfqOpen} onOpenChange={setRfqOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>RFQ - Tedarikçi Seç</DialogTitle>
                        <DialogDescription>Teklif almak istediğiniz tedarikçileri seçin</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                        {suppliers.filter(s=>s.active).map((s) => (
                            <label key={s.id} className="flex items-center gap-3 p-2 rounded border border-border cursor-pointer hover:bg-muted">
                                <input
                                    type="checkbox"
                                    data-testid={`rfq-sup-${s.code}`}
                                    checked={rfqSuppliers.includes(s.id)}
                                    onChange={(e) => setRfqSuppliers(e.target.checked ? [...rfqSuppliers, s.id] : rfqSuppliers.filter(id => id !== s.id))}
                                />
                                <div className="flex-1">
                                    <div className="font-medium text-sm">{s.company_name}</div>
                                    <div className="text-xs text-muted-foreground">{s.code} • Vade: {s.payment_term_days}g • Puan: {(s.performance?.rating||0).toFixed(1)}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setRfqOpen(false)}>İptal</Button>
                        <Button data-testid="create-rfq-btn" onClick={createRfq} disabled={rfqSuppliers.length===0}>
                            RFQ Oluştur ({rfqSuppliers.length})
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}

function Info({label,value}){return<div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="mt-0.5 text-sm font-medium">{value}</div></div>}

// ─────────────────────────────────────────────────────────────────────────
// Multi-Level Approval — chain visualization
// ─────────────────────────────────────────────────────────────────────────
const LEVEL_LABEL_TR = { single: "Tek Onay", manager: "Müdür Onayı", admin: "Yönetici Onayı" };

function ApprovalChainCard({ detail }) {
    const required = detail.approvals_required || [];
    const done = detail.approvals_done || [];
    const rejection = detail.rejection;
    const total = detail.estimated_total_try;
    const awarded = detail.awarded_total_try;
    const mixed = detail.estimated_total_mixed;
    const escalationReason = detail.escalation_reason;

    if (required.length === 0 && !rejection) return null;

    // Build chain rendering: each required slot matched with a done record (in order)
    const doneStack = [...done];
    const cells = required.map((lvl, i) => {
        const idx = doneStack.findIndex(d => d.level === lvl);
        const rec = idx >= 0 ? doneStack.splice(idx, 1)[0] : null;
        return { lvl, rec, i };
    });

    return (
        <div className="border border-border rounded-md p-3 bg-muted/20 space-y-2" data-testid="approval-chain">
            <div className="flex flex-wrap items-center gap-3 text-xs">
                <span><b>Talep Tutarı:</b>{" "}
                    {mixed ? (
                        <span className="text-amber-700">Karışık para birimi — güvenilir TL karşılığı hesaplanamadı</span>
                    ) : (
                        <span className="tabular-nums">{total != null ? new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }).format(total) : "—"} TL</span>
                    )}
                </span>
                {awarded != null && (
                    <span data-testid="awarded-total">
                        <b>Kazanan Teklif:</b>{" "}
                        <span className="tabular-nums">{new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }).format(awarded)} TL</span>
                    </span>
                )}
            </div>

            {escalationReason && (
                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1.5" data-testid="escalation-banner">
                    <b>Ek Onay Gerekiyor:</b> {escalationReason}
                </div>
            )}

            <div className="flex items-stretch gap-2 overflow-x-auto">
                {cells.map(({ lvl, rec, i }) => {
                    const isDone = !!rec;
                    const isPending = !isDone && cells.slice(0, i).every(c => !!c.rec);
                    return (
                        <div key={i} className={
                            "flex-1 min-w-[160px] rounded-md border p-2 text-xs " +
                            (isDone ? "border-emerald-300 bg-emerald-50" :
                             isPending ? "border-amber-300 bg-amber-50" :
                                         "border-border bg-background/50")
                        } data-testid={`approval-step-${i}`}>
                            <div className="font-medium text-[11px] uppercase tracking-wider">
                                {i + 1}. {LEVEL_LABEL_TR[lvl] || lvl}
                            </div>
                            {isDone ? (
                                <div className="mt-1 space-y-0.5">
                                    <div>✅ <b>{rec.approver_name}</b> ({rec.approver_role})</div>
                                    <div className="text-muted-foreground">{new Date(rec.at).toLocaleString("tr-TR")}</div>
                                </div>
                            ) : isPending ? (
                                <div className="mt-1 text-amber-700">⏳ Bekliyor</div>
                            ) : (
                                <div className="mt-1 text-muted-foreground">Sırada</div>
                            )}
                        </div>
                    );
                })}
            </div>

            {rejection && (
                <div className="border border-rose-300 bg-rose-50 rounded-md p-2 text-xs" data-testid="rejection-card">
                    <div className="font-medium text-rose-900">❌ Reddedildi</div>
                    <div className="mt-1"><b>Kim:</b> {rejection.approver_name} ({rejection.approver_role})</div>
                    <div><b>Tarih:</b> {new Date(rejection.at).toLocaleString("tr-TR")}</div>
                    <div className="mt-1 whitespace-pre-wrap"><b>Gerekçe:</b> {rejection.reason}</div>
                </div>
            )}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// Yeni Satın Alma Talebi — production-grade form
// ─────────────────────────────────────────────────────────────────────────────

const DEPT_SUGGESTIONS = [
    "Satınalma", "Üretim", "Ar-Ge", "Kalite", "Bakım-Onarım",
    "Depo", "Muhasebe", "İK", "İdari İşler", "Yönetim",
];
const UNITS = ["Adet", "Kg", "Ton", "Metre", "m²", "m³", "Takım", "Paket", "Kutu", "Litre"];
const CURRENCIES = ["TRY", "USD", "EUR"];
const PRIORITY_OPTS = [
    { v: "low", t: "Düşük" }, { v: "normal", t: "Normal" },
    { v: "high", t: "Yüksek" }, { v: "urgent", t: "Acil" },
];
const CCY_SYMBOL = { TRY: "₺", USD: "$", EUR: "€" };

const todayISO = () => new Date().toISOString().slice(0, 10);
const newLineId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

function makeEmptyLine() {
    return {
        _tmp: newLineId(),
        product_id: "",       // "" = catalog placeholder; "free:<uuid>" = free-text
        sku: "",
        name: "",
        description: "",
        unit: "Adet",
        quantity: 1,
        estimated_price: 0,
        currency: "TRY",
        brand_model: "",
        alternative_ok: null,  // null / true / false
        is_free_text: false,
    };
}

function NewPRButton({ open, setOpen, user, products, projects, onSubmit }) {
    const isRequester = user?.role === "requester";
    const activeProjects = React.useMemo(
        () => (projects || []).filter(p => p.status === "active" || !p.status),
        [projects]
    );

    // Header fields
    const [department, setDepartment] = useState(user?.department || "");
    const [projectId, setProjectId] = useState("");          // "" → Genel Gider / Projesiz
    const [priority, setPriority] = useState("normal");
    const [urgencyReason, setUrgencyReason] = useState("");
    const [requiredDate, setRequiredDate] = useState("");
    const [deliveryLocation, setDeliveryLocation] = useState("");
    const [notes, setNotes] = useState("");
    const [lines, setLines] = useState([makeEmptyLine()]);
    const [files, setFiles] = useState([]);   // File[]
    const [busy, setBusy] = useState(false);
    const fileRef = React.useRef(null);

    // Sync department when user profile arrives
    useEffect(() => {
        if (open) {
            setDepartment(user?.department || "");
        }
    }, [open, user?.department]);

    const resetForm = () => {
        setDepartment(user?.department || "");
        setProjectId(""); setPriority("normal"); setUrgencyReason("");
        setRequiredDate(""); setDeliveryLocation(""); setNotes("");
        setLines([makeEmptyLine()]); setFiles([]);
    };

    // ---- Line helpers ---------------------------------------------------------
    const addLine = () => setLines([...lines, makeEmptyLine()]);
    const rmLine = (idx) => {
        if (lines.length === 1) { setLines([makeEmptyLine()]); return; }
        setLines(lines.filter((_, i) => i !== idx));
    };
    const updLine = (idx, patch) => {
        setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    };
    const pickProduct = (idx, productId) => {
        const p = products.find(x => x.id === productId);
        if (!p) return;
        updLine(idx, {
            product_id: p.id, sku: p.sku, name: p.name,
            unit: p.unit ? p.unit.charAt(0).toUpperCase() + p.unit.slice(1) : "Adet",
            estimated_price: p.last_price || 0,
            currency: p.currency || "TRY",
            is_free_text: false,
        });
    };
    const toggleFreeText = (idx) => {
        const cur = lines[idx];
        if (cur.is_free_text) {
            // switch back to catalog: clear name/sku so user picks
            updLine(idx, { is_free_text: false, product_id: "", sku: "", name: "" });
        } else {
            updLine(idx, {
                is_free_text: true,
                product_id: `free:${newLineId()}`,
                sku: "—", name: "",
            });
        }
    };

    // ---- Files ----------------------------------------------------------------
    const addFiles = (e) => {
        const chosen = Array.from(e.target.files || []);
        const ok = [];
        for (const f of chosen) {
            if (f.size > 10 * 1024 * 1024) {
                toast.error(`${f.name}: 10 MB'dan büyük olamaz`);
                continue;
            }
            ok.push(f);
        }
        setFiles([...files, ...ok]);
        if (fileRef.current) fileRef.current.value = "";
    };
    const rmFile = (i) => setFiles(files.filter((_, j) => j !== i));

    // ---- Summary --------------------------------------------------------------
    const summary = React.useMemo(() => {
        const totals = {};
        let lineCount = 0;
        for (const l of lines) {
            const qty = Number(l.quantity) || 0;
            const price = Number(l.estimated_price) || 0;
            if (!l.name && !l.product_id) continue; // skip fully blank
            lineCount += 1;
            const ccy = l.currency || "TRY";
            totals[ccy] = (totals[ccy] || 0) + qty * price;
        }
        return { lineCount, totals };
    }, [lines]);

    // ---- Validation -----------------------------------------------------------
    const validate = (submitNow) => {
        const errors = [];
        if (requiredDate && requiredDate < todayISO()) {
            errors.push("Gerekli tarih geçmiş bir tarih olamaz");
        }
        if (priority === "urgent" && !urgencyReason.trim()) {
            errors.push("Acil öncelik için gerekçe zorunludur");
        }
        // Line-level
        const cleanedLines = [];
        lines.forEach((l, i) => {
            const idx = i + 1;
            const isBlank = !l.name?.trim() && !l.product_id;
            if (isBlank) return;
            if (!l.name?.trim()) errors.push(`${idx}. kalemde ürün adı zorunludur`);
            const qty = Number(l.quantity);
            if (!(qty > 0)) errors.push(`${idx}. kalemde miktar sıfırdan büyük olmalıdır`);
            if (Number(l.estimated_price || 0) < 0) errors.push(`${idx}. kalemde birim fiyat negatif olamaz`);
            if (!CURRENCIES.includes(l.currency)) errors.push(`${idx}. kalemde geçersiz para birimi`);
            cleanedLines.push(l);
        });
        if (submitNow && cleanedLines.length === 0) {
            errors.push("Onaya göndermek için en az bir kalem gerekli");
        }
        return { errors, cleanedLines };
    };

    // ---- Save -----------------------------------------------------------------
    const doSave = async (submitNow) => {
        if (busy) return;
        const { errors, cleanedLines } = validate(submitNow);
        if (errors.length) { toast.error(errors[0]); return; }

        const payload = {
            department: isRequester ? undefined : (department || undefined),
            project_id: projectId || null,
            priority,
            urgency_reason: priority === "urgent" ? urgencyReason.trim() : null,
            required_date: requiredDate || null,
            delivery_location: deliveryLocation.trim() || null,
            notes: notes.trim() || null,
            lines: cleanedLines.map(l => ({
                product_id: l.product_id || `free:${newLineId()}`,
                sku: l.sku || "—",
                name: l.name.trim(),
                unit: l.unit || "Adet",
                quantity: Number(l.quantity),
                estimated_price: Number(l.estimated_price || 0),
                notes: null,
                description: l.description?.trim() || null,
                currency: l.currency || "TRY",
                brand_model: l.brand_model?.trim() || null,
                alternative_ok: l.alternative_ok === null ? null : !!l.alternative_ok,
                is_free_text: !!l.is_free_text,
            })),
        };

        setBusy(true);
        try {
            await onSubmit({ payload, files, submitNow });
            resetForm();
        } finally {
            setBusy(false);
        }
    };

    // Product search index (client-side filter — small dataset, fine)
    const [prodQuery, setProdQuery] = useState({});   // {lineIdx: query}
    const filterProducts = (q) => {
        if (!q) return products.slice(0, 30);
        const s = q.toLowerCase();
        return products.filter(p =>
            p.name?.toLowerCase().includes(s) ||
            p.sku?.toLowerCase().includes(s) ||
            (p.brand || "").toLowerCase().includes(s)
        ).slice(0, 30);
    };

    return (
        <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
                <Button data-testid="new-pr-btn" className="gap-2"><Plus size={16}/> Yeni Talep</Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Yeni Satın Alma Talebi</DialogTitle>
                    <DialogDescription>Zorunlu alanları doldurup taslak olarak kaydedebilir veya doğrudan onaya gönderebilirsiniz.</DialogDescription>
                </DialogHeader>

                {/* HEADER */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <F label="Talep Eden">
                        <Input data-testid="pr-requested-by" value={`${user?.name || ""}${user?.email ? ` (${user.email})` : ""}`} readOnly disabled/>
                    </F>
                    <F label="Departman">
                        {isRequester ? (
                            <Input data-testid="pr-department-locked" value={department || "—"} readOnly disabled title="Profilinizdeki departman kullanılır"/>
                        ) : (
                            <div className="flex gap-1">
                                <select
                                    data-testid="pr-department"
                                    className="flex-1 h-10 border border-input rounded-md px-3 bg-background text-sm"
                                    value={department}
                                    onChange={(e)=>setDepartment(e.target.value)}
                                >
                                    <option value="">— Seçiniz —</option>
                                    {[...new Set([...(user?.department ? [user.department] : []), ...DEPT_SUGGESTIONS])].map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </F>
                    <F label="Proje">
                        <select
                            data-testid="pr-project"
                            className="h-10 border border-input rounded-md px-3 bg-background text-sm"
                            value={projectId}
                            onChange={(e)=>setProjectId(e.target.value)}
                        >
                            <option value="">Genel Gider / Projesiz</option>
                            {activeProjects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} — ` : ""}{p.name}</option>)}
                        </select>
                    </F>
                    <F label="Öncelik">
                        <select
                            data-testid="pr-priority"
                            className="h-10 border border-input rounded-md px-3 bg-background text-sm"
                            value={priority}
                            onChange={(e)=>setPriority(e.target.value)}
                        >
                            {PRIORITY_OPTS.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                        </select>
                    </F>
                    <F label="Gerekli Tarih">
                        <Input
                            data-testid="pr-required-date"
                            type="date"
                            min={todayISO()}
                            value={requiredDate}
                            onChange={(e)=>setRequiredDate(e.target.value)}
                        />
                    </F>
                    <F label="Teslim Yeri / Lokasyon">
                        <Input
                            data-testid="pr-delivery-location"
                            value={deliveryLocation}
                            onChange={(e)=>setDeliveryLocation(e.target.value)}
                            placeholder="Merkez Depo, Şantiye A, vb."
                        />
                    </F>
                </div>

                {priority === "urgent" && (
                    <F label="Acil Talep Gerekçesi (zorunlu)">
                        <Textarea
                            data-testid="pr-urgency-reason"
                            rows={2}
                            value={urgencyReason}
                            onChange={(e)=>setUrgencyReason(e.target.value)}
                            placeholder="Örn: Üretim hattı durdu, kritik yedek parça"
                        />
                    </F>
                )}

                {/* LINES */}
                <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Talep Kalemleri</div>
                        <Button size="sm" variant="outline" onClick={addLine} data-testid="pr-add-line">
                            <Plus size={14} className="mr-1"/> Kalem Ekle
                        </Button>
                    </div>
                    <div className="space-y-3">
                        {lines.map((l, i) => {
                            const ccy = l.currency || "TRY";
                            const lineTotal = (Number(l.quantity) || 0) * (Number(l.estimated_price) || 0);
                            const q = prodQuery[i] ?? "";
                            const matches = !l.is_free_text ? filterProducts(q) : [];
                            return (
                                <div key={l._tmp} className="border border-border rounded-md p-3 bg-muted/20" data-testid={`pr-line-${i}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs font-medium text-muted-foreground">Kalem #{i + 1}</div>
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs flex items-center gap-1 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    data-testid={`pr-line-${i}-freetext`}
                                                    checked={!!l.is_free_text}
                                                    onChange={()=>toggleFreeText(i)}
                                                /> Katalog dışı
                                            </label>
                                            <Button size="icon" variant="ghost" onClick={()=>rmLine(i)} data-testid={`pr-line-${i}-remove`}>
                                                <Trash2 size={14}/>
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                                        {/* Ürün */}
                                        <div className="md:col-span-5">
                                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Ürün / Malzeme *</Label>
                                            {!l.is_free_text ? (
                                                <>
                                                    <Input
                                                        data-testid={`pr-line-${i}-product-search`}
                                                        placeholder="Ürün ara: ad, SKU, marka..."
                                                        value={l.product_id ? `${l.sku} — ${l.name}` : q}
                                                        onChange={(e)=>{
                                                            setProdQuery({ ...prodQuery, [i]: e.target.value });
                                                            if (l.product_id) updLine(i, { product_id: "", sku: "", name: "" });
                                                        }}
                                                        className="h-9"
                                                    />
                                                    {!l.product_id && q && matches.length > 0 && (
                                                        <div className="border border-border rounded-md bg-card mt-1 max-h-40 overflow-y-auto shadow-sm">
                                                            {matches.map(p => (
                                                                <div
                                                                    key={p.id}
                                                                    data-testid={`pr-line-${i}-product-opt-${p.sku}`}
                                                                    className="px-2 py-1.5 text-xs cursor-pointer hover:bg-muted"
                                                                    onClick={()=>{ pickProduct(i, p.id); setProdQuery({ ...prodQuery, [i]: "" }); }}
                                                                >
                                                                    <span className="font-mono text-[10px] text-muted-foreground">{p.sku}</span> {p.name}
                                                                    {p.brand ? <span className="text-muted-foreground"> · {p.brand}</span> : null}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {!l.product_id && q && matches.length === 0 && (
                                                        <div className="text-[11px] text-muted-foreground mt-1">
                                                            Sonuç yok. <button className="underline" onClick={()=>toggleFreeText(i)}>Katalog dışı olarak gir</button>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <Input
                                                    data-testid={`pr-line-${i}-name`}
                                                    value={l.name}
                                                    onChange={(e)=>updLine(i, { name: e.target.value })}
                                                    placeholder="Serbest malzeme adı"
                                                    className="h-9"
                                                />
                                            )}
                                        </div>

                                        {/* Teknik Açıklama */}
                                        <div className="md:col-span-7">
                                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Teknik Açıklama / Özellik</Label>
                                            <Input
                                                data-testid={`pr-line-${i}-description`}
                                                value={l.description}
                                                onChange={(e)=>updLine(i, { description: e.target.value })}
                                                placeholder="Örn: Ø20 mm, S275 kalitesi, çift taraflı"
                                                className="h-9"
                                            />
                                        </div>

                                        {/* Miktar / Birim / Fiyat / Para */}
                                        <div className="md:col-span-2">
                                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Miktar *</Label>
                                            <Input
                                                data-testid={`pr-line-${i}-quantity`}
                                                type="number" min="0" step="0.01"
                                                value={l.quantity}
                                                onChange={(e)=>updLine(i, { quantity: e.target.value })}
                                                className="h-9 text-right"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Birim</Label>
                                            <select
                                                data-testid={`pr-line-${i}-unit`}
                                                className="w-full h-9 border border-input rounded-md px-2 bg-background text-sm"
                                                value={l.unit} onChange={(e)=>updLine(i, { unit: e.target.value })}
                                            >
                                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                            </select>
                                        </div>
                                        <div className="md:col-span-3">
                                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Birim Fiyat</Label>
                                            <Input
                                                data-testid={`pr-line-${i}-price`}
                                                type="number" min="0" step="0.01"
                                                value={l.estimated_price}
                                                onChange={(e)=>updLine(i, { estimated_price: e.target.value })}
                                                className="h-9 text-right"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Para</Label>
                                            <select
                                                data-testid={`pr-line-${i}-currency`}
                                                className="w-full h-9 border border-input rounded-md px-2 bg-background text-sm"
                                                value={l.currency} onChange={(e)=>updLine(i, { currency: e.target.value })}
                                            >
                                                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div className="md:col-span-3">
                                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Satır Toplamı</Label>
                                            <div
                                                data-testid={`pr-line-${i}-total`}
                                                className="h-9 flex items-center justify-end px-3 border border-dashed border-border rounded-md bg-background/50 text-sm tabular-nums font-medium"
                                            >
                                                {fmtMoney(lineTotal, ccy)}
                                            </div>
                                        </div>

                                        {/* Marka + Alternatif */}
                                        <div className="md:col-span-6">
                                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">İstenen Marka / Model</Label>
                                            <Input
                                                data-testid={`pr-line-${i}-brand`}
                                                value={l.brand_model}
                                                onChange={(e)=>updLine(i, { brand_model: e.target.value })}
                                                placeholder="Boş bırakılabilir"
                                                className="h-9"
                                            />
                                        </div>
                                        <div className="md:col-span-6">
                                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Alternatif Ürün Kabul Edilir mi?</Label>
                                            <div className="flex gap-2 h-9 items-center">
                                                {[["Evet", true], ["Hayır", false], ["—", null]].map(([label, val]) => (
                                                    <label key={String(val)} className="flex items-center gap-1 text-xs cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            data-testid={`pr-line-${i}-alt-${String(val)}`}
                                                            name={`alt-${l._tmp}`}
                                                            checked={l.alternative_ok === val}
                                                            onChange={()=>updLine(i, { alternative_ok: val })}
                                                        />
                                                        {label}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ATTACHMENTS (staged) */}
                <div className="mt-4 border border-border rounded-md p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Ekler (PDF, Excel, Word, JPG, PNG — max 10 MB / dosya)</div>
                        <input
                            ref={fileRef}
                            type="file"
                            multiple
                            data-testid="pr-files-input"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.dwg,.zip,application/pdf,image/*"
                            className="hidden"
                            onChange={addFiles}
                        />
                        <Button size="sm" variant="outline" onClick={()=>fileRef.current?.click()} data-testid="pr-files-btn">
                            <Plus size={13} className="mr-1"/> Dosya Ekle
                        </Button>
                    </div>
                    {files.length === 0 ? (
                        <div className="text-xs text-muted-foreground py-2">Henüz dosya eklenmedi.</div>
                    ) : (
                        <div className="space-y-1">
                            {files.map((f, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm bg-background border border-border rounded px-2 py-1" data-testid={`pr-file-${i}`}>
                                    <FileText size={14} className="text-muted-foreground"/>
                                    <span className="flex-1 truncate">{f.name}</span>
                                    <span className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(1)} KB</span>
                                    <Button size="icon" variant="ghost" onClick={()=>rmFile(i)} data-testid={`pr-file-${i}-remove`}>
                                        <Trash2 size={13}/>
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <F label="Notlar">
                    <Textarea data-testid="pr-notes" value={notes} onChange={(e)=>setNotes(e.target.value)} rows={2} placeholder="Serbest notlar (opsiyonel)"/>
                </F>

                {/* SUMMARY */}
                <div className="mt-2 border border-border rounded-md p-3 bg-muted/30" data-testid="pr-summary">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Talep Özeti</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-muted-foreground">Kalem:</span> <b data-testid="pr-summary-count">{summary.lineCount}</b></div>
                        <div><span className="text-muted-foreground">Proje:</span> <b>{activeProjects.find(p=>p.id===projectId)?.name || "Genel Gider / Projesiz"}</b></div>
                        <div><span className="text-muted-foreground">Öncelik:</span> <b>{PRIORITY_OPTS.find(o=>o.v===priority)?.t}</b></div>
                        <div><span className="text-muted-foreground">Gerekli Tarih:</span> <b>{requiredDate || "—"}</b></div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm">
                        {Object.keys(summary.totals).length === 0 ? (
                            <span className="text-muted-foreground">Tahmini toplam hesaplanmadı</span>
                        ) : (
                            Object.entries(summary.totals).map(([ccy, val]) => (
                                <div key={ccy} className="bg-background border border-border rounded px-2 py-1" data-testid={`pr-summary-total-${ccy}`}>
                                    <span className="text-muted-foreground text-xs">Toplam ({ccy}):</span>{" "}
                                    <b className="tabular-nums">{fmtMoney(val, ccy)}</b>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <DialogFooter className="gap-2 flex-wrap mt-3">
                    <Button variant="ghost" onClick={()=>setOpen(false)} data-testid="pr-cancel" disabled={busy}>İptal</Button>
                    <Button variant="outline" onClick={()=>doSave(false)} data-testid="pr-save-draft" disabled={busy}>
                        {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : null}
                        Taslak Kaydet
                    </Button>
                    <Button onClick={()=>doSave(true)} data-testid="pr-submit-now" disabled={busy}>
                        {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Send size={14} className="mr-1"/>}
                        Onaya Gönder
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
function F({label,children}){return<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>}
